import { Link, useParams } from "react-router";
import { MediaShowcase } from "../components/MediaShowcase";
import { PlatformIcon } from "../components/PlatformIcon";
import { WebGLFrame } from "../components/WebGLFrame";
import { projects } from "../data/projects";
import { NotFoundPage } from "./NotFoundPage";

const LABEL_ROW =
  "grid gap-8 border-b border-zinc-800 py-10 last:border-b-0 lg:grid-cols-[12rem_minmax(0,1fr)]";
const LABEL =
  "font-mono text-[9px] tracking-[0.22em] text-zinc-600 max-lg:border-b max-lg:border-zinc-800 max-lg:pb-3";

export function ProjectPage() {
  const { slug } = useParams();
  const project = projects.find((entry) => entry.slug === slug);

  if (!project) {
    return <NotFoundPage />;
  }

  const credits = [
    { label: "STUDIO", value: "PROJECT DESPAIR" },
    { label: "LEAD", value: "FRAZERIP" },
    ...(project.credits ?? []),
  ];

  return (
    <article className="mx-auto w-full max-w-6xl px-5 py-6 sm:p-10 lg:p-12">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-5">
        <p className="font-mono text-[9px] tracking-[0.22em] text-zinc-600">
          GAME // {project.index}
        </p>
        <Link
          to="/games"
          className="font-mono text-[9px] tracking-[0.16em] text-zinc-600 transition-colors hover:text-zinc-100 focus-visible:text-zinc-100 focus-visible:outline-none"
        >
          ← RETURN TO INDEX
        </Link>
      </div>

      <header className="grid gap-8 border-b border-zinc-800 py-12 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div>
          <p className="font-mono text-[9px] tracking-[0.22em] text-zinc-600">
            {project.subtitle.toUpperCase()}
          </p>
          <h1 className="mt-5 max-w-4xl text-balance text-[34px] font-light uppercase leading-[1.02] tracking-[-0.055em] text-zinc-100 sm:text-[52px] lg:text-[72px] lg:leading-[0.95]">
            {project.title}
          </h1>
          <p className="mt-7 max-w-2xl text-lg font-light leading-8 text-zinc-400">
            {project.summary}
          </p>
        </div>

        <dl className="self-start border border-zinc-800 bg-[#0b0b0b]">
          {[
            ["STATUS", project.status],
            ["YEAR", project.year],
            ["ENGINE", project.engine],
          ].map(([label, value]) => (
            <div
              key={label}
              className="grid grid-cols-[5.5rem_1fr] border-b border-zinc-800 last:border-b-0"
            >
              <dt className="border-r border-zinc-800 px-3 py-4 font-mono text-[8px] tracking-[0.18em] text-zinc-700">
                {label}
              </dt>
              <dd className="px-3 py-4 font-mono text-[9px] tracking-[0.12em] text-zinc-400">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </header>

      <MediaShowcase project={project} />

      <div className={LABEL_ROW}>
        <p className={LABEL}>OVERVIEW</p>
        <div className="flex max-w-3xl flex-col gap-5">
          {project.description.map((paragraph) => (
            <p key={paragraph} className="text-base font-light leading-8 text-zinc-400">
              {paragraph}
            </p>
          ))}

          <ul className="flex flex-wrap gap-2 pt-3" aria-label="Project tags">
            {project.tags.map((tag) => (
              <li
                key={tag}
                className="border border-zinc-800 px-3 py-2 font-mono text-[8px] tracking-[0.16em] text-zinc-600"
              >
                {tag}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {project.highlights?.length ? (
        <div className={LABEL_ROW}>
          <p className={LABEL}>HIGHLIGHTS</p>
          <ul className="flex max-w-3xl flex-col gap-px border border-zinc-800 bg-zinc-800">
            {project.highlights.map((highlight, index) => (
              <li
                key={highlight}
                className="grid grid-cols-[3rem_minmax(0,1fr)] bg-[#0b0b0b]"
              >
                <span className="border-r border-zinc-800 px-3 py-4 font-mono text-[8px] tracking-[0.16em] text-zinc-700">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="p-4 text-[15px] font-light leading-7 text-zinc-400">
                  {highlight}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {project.links?.length ? (
        <div className={LABEL_ROW}>
          <p className={LABEL}>LINKS</p>
          <div className="flex flex-wrap gap-3">
            {project.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2.5 border border-zinc-700 px-4 py-3 font-mono text-[9px] tracking-[0.16em] text-zinc-400 transition-colors hover:border-zinc-100 hover:bg-zinc-100 hover:text-black focus-visible:border-zinc-100 focus-visible:bg-zinc-100 focus-visible:text-black focus-visible:outline-none"
              >
                {link.icon ? <PlatformIcon icon={link.icon} className="h-3.5 w-3.5 shrink-0" /> : null}
                {link.label} ↗
              </a>
            ))}
          </div>
        </div>
      ) : null}

      {project.linkNote ? (
        <div className={LABEL_ROW}>
          <p className={LABEL}>LINKS</p>
          <p className="border border-dashed border-zinc-700 px-5 py-4 font-mono text-[9px] tracking-[0.16em] text-zinc-600">
            {project.linkNote}
          </p>
        </div>
      ) : null}

      <WebGLFrame project={project} />

      <div className={LABEL_ROW}>
        <p className={LABEL}>STAFFS</p>
        <dl className="max-w-3xl border border-zinc-800 bg-[#0b0b0b]">
          {credits.map((credit) => (
            <div
              key={`${credit.label}-${credit.value}`}
              className="grid grid-cols-[7rem_minmax(0,1fr)] border-b border-zinc-800 last:border-b-0"
            >
              <dt className="border-r border-zinc-800 px-3 py-4 font-mono text-[8px] tracking-[0.18em] text-zinc-700">
                {credit.label}
              </dt>
              <dd className="px-3 py-4 font-mono text-[9px] tracking-[0.12em] text-zinc-400">
                {credit.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {project.thanks?.length ? (
        <div className={LABEL_ROW}>
          <p className={LABEL}>CREDITS &amp; SPECIAL THANKS</p>
          <ul className="grid max-w-3xl gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2">
            {project.thanks.map((name) => (
              <li
                key={name}
                className="bg-[#0b0b0b] px-4 py-3.5 font-mono text-[9px] tracking-[0.12em] text-zinc-400"
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}
